<template>
  <div v-if="viewModal" class="my-modal">
    <fade-transition>
      <div
        v-if="delayShowModal"
        class="my-modal__backdrop"
        @click="closeOnBackdrop && close()"
      ></div>
    </fade-transition>
    <zoom-center-transition>
      <div
        v-if="delayShowModal"
        :class="`my-modal__content my-modal__content--${size} ${contentClass}`"
      >
        <div
          v-if="hasHeader"
          class="my-modal__header"
          :class="{ 'box-shadow--header': showHeaderShadow }"
        >
          <span v-show="hasClose" class="my-modal__close" @click="close()">
            <mdicon name="close" size="20" />
          </span>
          <span class="my-modal__title">{{ title }}</span>
        </div>
        <div
          :class="`my-modal__body ${bodyClass}`"
          id="check-scroll"
          @scroll="checkScroll($event)"
        >
          <slot name="body"></slot>
        </div>
        <div
          v-if="hasFooter"
          class="my-modal__footer"
          :class="{ 'box-shadow--floating-button': showFooterShadow }"
        >
          <slot name="footer">
            <Button
              v-if="cancelButton"
              contentClass="justify-content-center"
              variant="filled-complementary"
              :text="$lang.cancel"
              @click="close()"
            />
            <Button
              v-if="okButton"
              class="margin-right-8"
              contentClass="justify-content-center"
              :variant="okButtonVariant"
              :text="okButtonText || $lang.submit"
              :loading="okLoading"
              @click="submit"
            />
          </slot>
        </div>
      </div>
    </zoom-center-transition>
  </div>
</template>

<script>
import { Button } from "@/components/re-design/forms";
import { ref, toRefs, watch } from "vue";
export default {
  components: { Button },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true,
    },
    hasHeader: {
      type: Boolean,
      default: true,
    },
    hasClose: {
      type: Boolean,
      default: false,
    },
    hasFooter: {
      type: Boolean,
      default: true,
    },
    okButton: {
      type: Boolean,
      default: false,
    },
    ok: {
      type: Function,
      default: () => {},
    },
    size: {
      type: String,
      default: "md",
    },
    okButtonVariant: {
      type: String,
      default: "filled-primary",
    },
    okButtonText: {
      type: String,
      default: null,
    },
    contentClass: {
      type: String,
      default: "",
    },
    cancelButton: {
      type: Boolean,
      default: true,
    },
    bodyClass: {
      type: String,
      default: "",
    },
  },
  setup(props, { emit }) {
    // generate props data
    const {
      show,
      title,
      closeOnBackdrop,
      hasHeader,
      hasClose,
      hasFooter,
      okButton,
      size,
      okButtonVariant,
      okButtonText,
      contentClass,
      cancelButton,
    } = toRefs(props);

    // show and hide modal with tracking show prop
    const viewModal = ref(false);
    const delayShowModal = ref(false);
    const close = () => {
      delayShowModal.value = false;
      setTimeout(() => emit("close"), 300);
    };
    watch(show, (value) => {
      if (value) {
        viewModal.value = true;
        setTimeout(() => (delayShowModal.value = true), 300);
      } else {
        delayShowModal.value = false;
        setTimeout(() => (viewModal.value = false), 300);
      }
    });

    // generate show header and footer shadow
    const showHeaderShadow = ref(false);
    const showFooterShadow = ref(false);
    const checkScroll = () => {
      const el = document.getElementById("check-scroll");
      const calcScroll = el.scrollHeight - el.scrollTop;
      showHeaderShadow.value = el.scrollTop;
      showFooterShadow.value = calcScroll > el.clientHeight;
    };

    // generate submit method
    const okLoading = ref(false);
    const submit = async () => {
      try {
        okLoading.value = true;
        // call ok method from props
        await props.ok();
      } catch (error) {
        //
      } finally {
        okLoading.value = false;
      }
    };

    // return data to be used in template
    return {
      title,
      closeOnBackdrop,
      hasHeader,
      hasClose,
      hasFooter,
      okButton,
      size,
      okButtonVariant,
      okButtonText,
      contentClass,
      cancelButton,
      viewModal,
      delayShowModal,
      okLoading,
      showHeaderShadow,
      showFooterShadow,
      checkScroll,
      submit,
      close,
    };
  },
};
</script>
